import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { assetsAPI, employeeAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Package, History } from 'lucide-react';
import { EmptyState, CardSkeleton } from './common/States';

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'under_repair', label: 'Under repair' },
  { value: 'lost', label: 'Lost' },
  { value: 'retired', label: 'Retired' },
];

const statusBadgeVariant = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'available') return 'success';
  if (normalized === 'assigned') return 'outline';
  if (normalized === 'under_repair') return 'secondary';
  if (normalized === 'lost') return 'destructive';
  if (normalized === 'retired') return 'secondary';
  return 'secondary';
};

const AssetManagementModule = ({ mode = 'employee' }) => {
  const canManage = mode === 'admin' || mode === 'manager';
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [assignedEmployeeId, setAssignedEmployeeId] = useState('all');

  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [events, setEvents] = useState([]);

  const [assetForm, setAssetForm] = useState({
    asset_tag: '',
    name: '',
    category: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    purchase_date: '',
    purchase_cost: '',
    status: 'available',
    location: '',
    description: '',
    notes: '',
  });

  const [assignForm, setAssignForm] = useState({ employee_id: '', notes: '' });

  const employeeLabelById = useMemo(() => {
    const map = new Map();
    employees.forEach((row) => {
      const id = row.employee_id || row.employeeId;
      if (!id) return;
      const fullName = [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(' ').trim();
      map.set(String(id), fullName ? `${fullName} (${id})` : String(id));
    });
    return map;
  }, [employees]);

  const resetAssetForm = () => {
    setAssetForm({
      asset_tag: '',
      name: '',
      category: '',
      manufacturer: '',
      model: '',
      serial_number: '',
      purchase_date: '',
      purchase_cost: '',
      status: 'available',
      location: '',
      description: '',
      notes: '',
    });
  };

  const loadEmployeesIfNeeded = async () => {
    if (!canManage) return;
    try {
      const res = await employeeAPI.list();
      setEmployees(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load employee directory'));
    }
  };

  const loadAssets = async () => {
    try {
      setLoading(true);
      if (canManage) {
        const params = {};
        if (status !== 'all') params.status = status;
        if (assignedEmployeeId !== 'all') params.assigned_employee_id = assignedEmployeeId;
        if (search.trim()) params.search = search.trim();
        const res = await assetsAPI.list(params);
        setAssets(res.data || []);
      } else {
        const res = await assetsAPI.listMy();
        setAssets(res.data || []);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load assets'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployeesIfNeeded();
    loadAssets();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; loaders are stable
  }, []);

  const openAssetDetails = async (row) => {
    setSelectedAsset(row);
    setShowDetails(true);
    if (!row?.id) return;
    try {
      const res = await assetsAPI.get(row.id);
      setSelectedAsset(res.data || row);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load asset details'));
    }
  };

  const openCreate = () => {
    resetAssetForm();
    setShowCreate(true);
  };

  const openEdit = (row) => {
    setSelectedAsset(row);
    setAssetForm({
      asset_tag: row.asset_tag || '',
      name: row.name || '',
      category: row.category || '',
      manufacturer: row.manufacturer || '',
      model: row.model || '',
      serial_number: row.serial_number || '',
      purchase_date: row.purchase_date || '',
      purchase_cost: row.purchase_cost != null ? String(row.purchase_cost) : '',
      status: row.status || 'available',
      location: row.location || '',
      description: row.description || '',
      notes: row.notes || '',
    });
    setShowEdit(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...assetForm,
        purchase_date: assetForm.purchase_date ? assetForm.purchase_date : null,
        purchase_cost: assetForm.purchase_cost !== '' ? Number(assetForm.purchase_cost) : null,
      };
      const res = await assetsAPI.create(payload);
      toast.success('Asset created');
      setShowCreate(false);
      setSelectedAsset(res.data || null);
      await loadAssets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create asset'));
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!selectedAsset?.id) return;
    try {
      const payload = {
        ...assetForm,
        purchase_date: assetForm.purchase_date ? assetForm.purchase_date : null,
        purchase_cost: assetForm.purchase_cost !== '' ? Number(assetForm.purchase_cost) : null,
      };
      const res = await assetsAPI.update(selectedAsset.id, payload);
      toast.success('Asset updated');
      setShowEdit(false);
      setSelectedAsset(res.data || null);
      await loadAssets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update asset'));
    }
  };

  const openAssign = (row) => {
    setSelectedAsset(row);
    setAssignForm({ employee_id: '', notes: '' });
    setShowAssign(true);
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    if (!selectedAsset?.id) return;
    try {
      await assetsAPI.assign(selectedAsset.id, {
        employee_id: assignForm.employee_id,
        notes: assignForm.notes || '',
      });
      toast.success('Asset assigned');
      setShowAssign(false);
      await loadAssets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign asset'));
    }
  };

  const returnAsset = async (row) => {
    if (!row?.id) return;
    try {
      await assetsAPI.return(row.id, {});
      toast.success('Asset returned');
      await loadAssets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to return asset'));
    }
  };

  const retireAsset = async (row) => {
    if (!row?.id) return;
    try {
      await assetsAPI.retire(row.id, {});
      toast.success('Asset retired');
      await loadAssets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to retire asset'));
    }
  };

  const openEvents = async (row) => {
    if (!row?.id) return;
    setSelectedAsset(row);
    setShowEvents(true);
    try {
      const res = await assetsAPI.events(row.id, { limit: 100 });
      setEvents(res.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load asset events'));
    }
  };

  const headerLabel = canManage ? 'Asset Management' : 'My Assets';
  const emptyText = canManage ? 'No assets found' : 'No assets assigned';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle>{headerLabel}</CardTitle>
            {canManage && (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={loadAssets} disabled={loading}>Refresh</Button>
                <Button onClick={openCreate}>New Asset</Button>
              </div>
            )}
            {!canManage && (
              <Button variant="outline" onClick={loadAssets} disabled={loading}>Refresh</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {canManage && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <Label>Search</Label>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Asset tag / name / serial" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assignee</Label>
                <Select value={assignedEmployeeId} onValueChange={setAssignedEmployeeId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {employees.map((row) => {
                      const id = row.employee_id || row.employeeId;
                      if (!id) return null;
                      return (
                        <SelectItem key={String(id)} value={String(id)}>
                          {employeeLabelById.get(String(id)) || String(id)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-4 flex items-center gap-2">
                <Button variant="outline" onClick={loadAssets} disabled={loading}>Apply</Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSearch('');
                    setStatus('all');
                    setAssignedEmployeeId('all');
                    setTimeout(loadAssets, 0);
                  }}
                  disabled={loading}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}

          {loading && assets.length === 0 ? (
            <CardSkeleton rows={5} />
          ) : assets.length === 0 ? (
            <EmptyState
              icon={Package}
              title={emptyText}
              description={canManage ? 'Create a new asset or adjust your filters to see results.' : 'You currently have no assets assigned to you.'}
              actionLabel={canManage ? 'New Asset' : undefined}
              onAction={canManage ? openCreate : undefined}
            />
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tag</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.asset_tag}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariant(row.status)}>{String(row.status || '').replace('_', ' ') || 'unknown'}</Badge>
                  </TableCell>
                  <TableCell>
                    {row.assigned_employee_id ? (employeeLabelById.get(String(row.assigned_employee_id)) || row.assigned_employee_id) : '-'}
                  </TableCell>
                  <TableCell>{row.location || '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => openAssetDetails(row)}>View</Button>
                      {canManage && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openEvents(row)}>Events</Button>
                          <Button size="sm" variant="outline" onClick={() => openEdit(row)}>Edit</Button>
                          <Button size="sm" onClick={() => openAssign(row)} disabled={String(row.status || '').toLowerCase() === 'retired'}>Assign</Button>
                          <Button size="sm" variant="outline" onClick={() => returnAsset(row)} disabled={!row.assigned_employee_id}>Return</Button>
                          <Button size="sm" variant="destructive" onClick={() => retireAsset(row)} disabled={String(row.status || '').toLowerCase() === 'retired'}>Retire</Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Asset Details</DialogTitle>
          </DialogHeader>
          {selectedAsset && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Asset Tag</Label>
                <Input value={selectedAsset.asset_tag || ''} readOnly />
              </div>
              <div>
                <Label>Status</Label>
                <Input value={selectedAsset.status || ''} readOnly />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={selectedAsset.name || ''} readOnly />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={selectedAsset.category || ''} readOnly />
              </div>
              <div>
                <Label>Serial</Label>
                <Input value={selectedAsset.serial_number || ''} readOnly />
              </div>
              <div>
                <Label>Assignee</Label>
                <Input value={selectedAsset.assigned_employee_id ? (employeeLabelById.get(String(selectedAsset.assigned_employee_id)) || selectedAsset.assigned_employee_id) : ''} readOnly />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={selectedAsset.location || ''} readOnly />
              </div>
              <div>
                <Label>Purchase Date</Label>
                <Input value={selectedAsset.purchase_date || ''} readOnly />
              </div>
              <div>
                <Label>Purchase Cost</Label>
                <Input value={selectedAsset.purchase_cost != null ? String(selectedAsset.purchase_cost) : ''} readOnly />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea value={selectedAsset.description || ''} readOnly />
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={selectedAsset.notes || ''} readOnly />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Asset</DialogTitle>
          </DialogHeader>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={submitCreate}>
            <div>
              <Label>Asset Tag</Label>
              <Input value={assetForm.asset_tag} onChange={(e) => setAssetForm((p) => ({ ...p, asset_tag: e.target.value }))} required />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={assetForm.name} onChange={(e) => setAssetForm((p) => ({ ...p, name: e.target.value }))} required />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={assetForm.status} onValueChange={(v) => setAssetForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Input value={assetForm.category} onChange={(e) => setAssetForm((p) => ({ ...p, category: e.target.value }))} />
            </div>
            <div>
              <Label>Manufacturer</Label>
              <Input value={assetForm.manufacturer} onChange={(e) => setAssetForm((p) => ({ ...p, manufacturer: e.target.value }))} />
            </div>
            <div>
              <Label>Model</Label>
              <Input value={assetForm.model} onChange={(e) => setAssetForm((p) => ({ ...p, model: e.target.value }))} />
            </div>
            <div>
              <Label>Serial Number</Label>
              <Input value={assetForm.serial_number} onChange={(e) => setAssetForm((p) => ({ ...p, serial_number: e.target.value }))} />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={assetForm.location} onChange={(e) => setAssetForm((p) => ({ ...p, location: e.target.value }))} />
            </div>
            <div>
              <Label>Purchase Date</Label>
              <Input type="date" value={assetForm.purchase_date || ''} onChange={(e) => setAssetForm((p) => ({ ...p, purchase_date: e.target.value }))} />
            </div>
            <div>
              <Label>Purchase Cost</Label>
              <Input type="number" value={assetForm.purchase_cost} onChange={(e) => setAssetForm((p) => ({ ...p, purchase_cost: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea value={assetForm.description} onChange={(e) => setAssetForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={assetForm.notes} onChange={(e) => setAssetForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit">Create</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
          </DialogHeader>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-3" onSubmit={submitEdit}>
            <div>
              <Label>Asset Tag</Label>
              <Input value={assetForm.asset_tag} onChange={(e) => setAssetForm((p) => ({ ...p, asset_tag: e.target.value }))} required />
            </div>
            <div>
              <Label>Name</Label>
              <Input value={assetForm.name} onChange={(e) => setAssetForm((p) => ({ ...p, name: e.target.value }))} required />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={assetForm.status} onValueChange={(v) => setAssetForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Input value={assetForm.category} onChange={(e) => setAssetForm((p) => ({ ...p, category: e.target.value }))} />
            </div>
            <div>
              <Label>Manufacturer</Label>
              <Input value={assetForm.manufacturer} onChange={(e) => setAssetForm((p) => ({ ...p, manufacturer: e.target.value }))} />
            </div>
            <div>
              <Label>Model</Label>
              <Input value={assetForm.model} onChange={(e) => setAssetForm((p) => ({ ...p, model: e.target.value }))} />
            </div>
            <div>
              <Label>Serial Number</Label>
              <Input value={assetForm.serial_number} onChange={(e) => setAssetForm((p) => ({ ...p, serial_number: e.target.value }))} />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={assetForm.location} onChange={(e) => setAssetForm((p) => ({ ...p, location: e.target.value }))} />
            </div>
            <div>
              <Label>Purchase Date</Label>
              <Input type="date" value={assetForm.purchase_date || ''} onChange={(e) => setAssetForm((p) => ({ ...p, purchase_date: e.target.value }))} />
            </div>
            <div>
              <Label>Purchase Cost</Label>
              <Input type="number" value={assetForm.purchase_cost} onChange={(e) => setAssetForm((p) => ({ ...p, purchase_cost: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea value={assetForm.description} onChange={(e) => setAssetForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={assetForm.notes} onChange={(e) => setAssetForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Assign Asset</DialogTitle>
          </DialogHeader>
          <form className="space-y-3" onSubmit={submitAssign}>
            <div>
              <Label>Employee</Label>
              <Select value={assignForm.employee_id} onValueChange={(v) => setAssignForm((p) => ({ ...p, employee_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((row) => {
                    const id = row.employee_id || row.employeeId;
                    if (!id) return null;
                    return (
                      <SelectItem key={String(id)} value={String(id)}>
                        {employeeLabelById.get(String(id)) || String(id)}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={assignForm.notes} onChange={(e) => setAssignForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
              <Button type="submit" disabled={!assignForm.employee_id}>Assign</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showEvents} onOpenChange={setShowEvents}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Asset Events</DialogTitle>
          </DialogHeader>
          {events.length === 0 ? (
            <EmptyState
              icon={History}
              compact
              title="No events yet"
              description="Asset lifecycle events such as assignments and returns will appear here."
            />
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.created_at ? String(row.created_at).replace('T', ' ').slice(0, 19) : '-'}</TableCell>
                  <TableCell>{row.action}</TableCell>
                  <TableCell>{row.from_employee_id ? (employeeLabelById.get(String(row.from_employee_id)) || row.from_employee_id) : '-'}</TableCell>
                  <TableCell>{row.to_employee_id ? (employeeLabelById.get(String(row.to_employee_id)) || row.to_employee_id) : '-'}</TableCell>
                  <TableCell className="max-w-[260px] truncate">{row.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssetManagementModule;
